import {
  PAGE_ENGINEERING,
  PAGE_HOME,
  PAGE_RESEARCH,
  PAGE_SIMULATION,
  PAGE_JOURNAL,
  PAGE_OUTPUTS,
  PAGE_BUILD,
} from '../../constants/pages';
import React from 'react';
import { faCircle } from '@whitebox-learning/fontawesome-pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const homePageSteps = [
  {
    disableBeacon: true,
    target: '.welcome',
    content: "This is the start page, you'll see it each time you login.",
    placement: 'center',
  },
  {
    target: '.welcome-buttons',
    content:
      'You can click any of these buttons to jump directly to a section.',
    placement: 'left',
  },
  {
    target: '#start-research',
    content:
      "Let's start with the research section.  Click the button to continue the tour.",
  },
];

const researchPageSteps = [
  {
    target: '.navbar',
    content:
      "First, let's take a look at the navigation bar at the top of the page.",
    disableBeacon: true,
  },
  {
    target: '.navbar #app-name-nav',
    content: 'You can click here at any time to go back to the start page.',
  },
  {
    target: '.navbar .game-status',
    content:
      'This shows your current level and a mini-progress bar for WhiteBox Nationals (Game Mode).  You can click the trophy icon to learn more about game mode, or click the progress bar to see more details.',
    filter: (params) => params.gameMode === true,
  },
  {
    target: '#main-nav',
    content: 'You can navigate between pages by clicking these links.',
  },
  {
    target: '.navbar .file-menu',
    content: 'These buttons open the File Manager, Tutorials, and Journal.',
  },
  {
    target: '.navbar .logout',
    content: "Click this button when you're ready to logout.",
  },
  {
    target: '.navbar .nav-research',
    content:
      "You are currently viewing the research page.  Let's take a closer look!",
  },
  {
    target: '.sidebar-nav',
    content:
      'Lessons, tutorials, quizzes and worksheets are listed on the left side of the Research page.',
    placement: 'right',
  },
  {
    target: '.sidebar-nav .sidebar-nav-buttons',
    content:
      'You can click these buttons to move through the research materials in the suggested order.',
    placement: 'top',
  },
  {
    target: '.navbar .nav-engineering',
    content:
      "Next we'll explore the Engineering page. Click here to go to Engineering.",
  },
];

const engineeringPageSteps = [
  {
    target: '.engineering-toolbar',
    content:
      "The Engineering page is where you'll edit and analyze your design.  The toolbar on the left side of the page controls which 'work areas' and graphics are displayed.  You can learn more about these tools in the UI Tutorial.",
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '.concept-status',
    styles: {
      tooltipContainer: {
        textAlign: 'left',
      },
    },
    content: (
      <div>
        <p>
          The name of your current design concept is displayed here. The color
          of the icon indicates the save status of your design.
        </p>
        <dl className="row">
          <dt className="col-1">
            <FontAwesomeIcon icon={faCircle} color="red" />
          </dt>
          <dd className="col-11">
            Red indicates the design has not been saved.
          </dd>
          <dt className="col-1">
            <FontAwesomeIcon icon={faCircle} color="#ffc107" />
          </dt>
          <dd className="col-11">
            Yellow indicates changes since the last save.
          </dd>
          <dt className="col-1">
            <FontAwesomeIcon icon={faCircle} color="green" />
          </dt>
          <dd className="col-11">
            Green indicates all changes have been saved.
          </dd>
        </dl>
      </div>
    ),
    placement: 'top',
  },
  {
    target: '.graphics-area',
    content:
      '2D and 3D Design components, charts, and other analysis tools are displayed in the graphics area as you enable them from the toolbar.',
    placement: 'center',
  },
];

// file manager steps
const fileManagerSteps = [
  {
    target: '.fm-simple-panel',
    content:
      'This is the file manager main menu.  From here you can start a new design concept, save your work, or open an existing design.',
    placement: 'left',
  },
  {
    target: '#fm-save-design-menu-button',
    content:
      "This option will save the concept you are working on. When saving your design, you'll also have the option to stage it for competition.",
    placement: 'left',
  },
  {
    target: '.navbar .nav-simulation',
    content:
      'In the Competition, you can test out your designs by competing them against other designs. Click here to take a look at the Competition page.',
    appPage: PAGE_ENGINEERING,
  },
];

// competition
const simulationSteps = [
  {
    target: '#simulation-setup-contenders',
    content:
      "On the Competition page, you can test your designs by competing against other designs. You can compete against other students' designs as well as your own. You'll start by choosing two contenders to compete, which will be shown here.",
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '#simulation-contenders-list',
    content:
      "You'll choose the designs you want to compete from this list. Your designs will appear here once they are staged.",
    placement: 'right',
  },
  {
    target: '#nav-journal',
    content:
      'After you see how your design performs, you can update its notes in your Journal. Click here to go to the Journal.',
    placement: 'bottom',
  },
];

// journal
const journalSteps = [
  {
    target: '.main-content',
    content:
      'This area is your Journal. Here, you can see detailed information and record notes about your design history as you iterate and innovate. In particular, the Engineering Logs can help you analyze your past designs.',
    placement: 'center',
  },
];

const outputSteps = [
  {
    target: '.navbar .nav-outputs',
    content:
      'Eventually, you will choose a design to build in the real world. The Outputs tab will provide all necessary outputs for your physical build.',
    placement: 'bottom',
    appPage: PAGE_OUTPUTS,
  },
  {
    target: '.sidebar-nav',
    content:
      'The Outputs menu contains reports, print templates, drawings, CNC, 3D-printer, and other important outputs depending on the application and your selections.',
    placement: 'right',
  },
];

const buildSteps = [
  {
    target: '.navbar .nav-build',
    content:
      'Finally, the Build page contains instructions for putting together your physical design.',
    placement: 'bottom',
    appPage: PAGE_BUILD,
  },
  {
    target: '.sidebar-nav',
    content:
      'The Build menu provides detailed instructions on how to build and test your physical model.',
    placement: 'right',
  },
  {
    target: '.main-content',
    content:
      "And that's it for the Tour! There is plenty to explore in WhiteBox Learning that this tour did not mention. Go back to the Research section to get started, and have fun!",
    placement: 'center',
  },
];

export const sections = {
  [PAGE_HOME]: homePageSteps,
  [PAGE_RESEARCH]: researchPageSteps,
  [PAGE_ENGINEERING]: engineeringPageSteps,
  file: fileManagerSteps,
  [PAGE_SIMULATION]: simulationSteps,
  [PAGE_JOURNAL]: journalSteps,
  [PAGE_OUTPUTS]: outputSteps,
  [PAGE_BUILD]: buildSteps,
};

export function assembleSteps(params) {
  let steps = [];

  for (const page in sections) {
    steps.push(
      ...sections[page]
        .filter((step) => {
          return !step.hasOwnProperty('filter') || step.filter(params);
        })
        .map((step) => ({ ...step, appPage: step.appPage || page }))
    );
  }

  return steps;
}
